import React, { useEffect, useState } from 'react'
import { AlertCircle, Bell, CheckCircle, Clock, Mail, Play, RefreshCw, Trash2, XCircle } from 'lucide-react'
import { useToast } from '@/hooks/useToast'
import { workAuthService } from '@/services/workAuthService'

interface TestEmployee {
  employeeId: string
  id: string
  name: string
  email: string
  managerEmail: string
  workAuth: {
    id: string
    expirationDate: string
    daysUntilExpiration: number
    currentStatus: string
    notificationCount: number
    lastNotification: {
      notificationType: string
      recipientEmail: string
      sentAt: string
    } | null
    uploadToken: string | null
  }
}

interface NotificationHistory {
  id: string
  notificationType: string
  recipientEmail: string
  sentAt: string
}

const WorkAuthNotificationTestingPage: React.FC = () => {
  const { toast } = useToast()
  const [testEmployees, setTestEmployees] = useState<TestEmployee[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<TestEmployee | null>(null)
  const [notificationHistory, setNotificationHistory] = useState<NotificationHistory[]>([])
  const [triggerLoading, setTriggerLoading] = useState(false)

  // Load test data on mount
  useEffect(() => {
    loadTestData()
  }, [])

  // Load notification history when employee is selected
  useEffect(() => {
    if (selectedEmployee) {
      loadNotificationHistory(selectedEmployee.workAuth.id)
    }
  }, [selectedEmployee])

  const loadTestData = async () => {
    try {
      setLoading(true)
      const response = await workAuthService.listTestData()
      console.log('📊 Loaded test data:', response)
      setTestEmployees(response.data || []) // Unwrap data and ensure it's always an array
    } catch (error) {
      console.error('Failed to load test data:', error)
      toast.error('Failed to load test data', 'Please try again')
      setTestEmployees([]) // Set empty array on error
    } finally {
      setLoading(false)
    }
  }

  const loadNotificationHistory = async (workAuthId: string) => {
    try {
      const data = await workAuthService.getNotificationHistory(workAuthId)
      setNotificationHistory(data)
    } catch (error) {
      console.error('Failed to load notification history:', error)
    }
  }

  const triggerNotification = async (employeeId: string, forceResend = false) => {
    try {
      setTriggerLoading(true)
      const result = await workAuthService.triggerManualNotifications({
        employeeId,
        forceResend
      })

      toast.success(
        'Notification Sent',
        `Processed ${result.processed} employee(s), sent ${result.sent} notification(s)`
      )

      // Reload data
      await loadTestData()
      if (selectedEmployee && selectedEmployee.employeeId === employeeId) {
        await loadNotificationHistory(selectedEmployee.workAuth.id)
      }
    } catch (error: any) {
      console.error('Failed to trigger notification:', error)
      toast.error(
        'Notification Failed',
        error.response?.data?.message || 'Failed to send notification'
      )
    } finally {
      setTriggerLoading(false)
    }
  }

  const triggerAllNotifications = async () => {
    try {
      setTriggerLoading(true)
      const result = await workAuthService.triggerManualNotifications()

      toast.success(
        'Notifications Sent',
        `Sent: ${result.sent}, Failed: ${result.failed}, Skipped: ${(result as any).skipped || 0}`
      )

      await loadTestData()
    } catch (error: any) {
      console.error('Failed to trigger notifications:', error)
      toast.error(
        'Notification Failed',
        error.response?.data?.message || 'Failed to send notifications'
      )
    } finally {
      setTriggerLoading(false)
    }
  }

  const cleanupTestData = async () => {
    if (!confirm('Are you sure you want to delete all test data? This cannot be undone.')) {
      return
    }

    try {
      setLoading(true)
      const result = await workAuthService.cleanupTestData()

      toast.success(
        'Cleanup Complete',
        `Deleted ${result.deletedEmployees} employee(s) and related data`
      )

      setTestEmployees([])
      setSelectedEmployee(null)
      setNotificationHistory([])
    } catch (error) {
      console.error('Failed to cleanup test data:', error)
      toast.error('Cleanup Failed', 'Failed to delete test data')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (days: number) => {
    if (days <= 0) return 'text-red-600 bg-red-50 border-red-200'
    if (days <= 30) return 'text-orange-600 bg-orange-50 border-orange-200'
    if (days <= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    return 'text-blue-600 bg-blue-50 border-blue-200'
  }

  const getStatusIcon = (days: number) => {
    if (days <= 0) return <XCircle className="h-5 w-5" />
    if (days <= 30) return <AlertCircle className="h-5 w-5" />
    if (days <= 60) return <Clock className="h-5 w-5" />
    return <CheckCircle className="h-5 w-5" />
  }

  const formatNotificationType = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ')
  }

  const copyUploadUrl = (token: string) => {
    const url = `${window.location.origin}/work-auth/upload/${token}`
    navigator.clipboard.writeText(url)
    toast.success('Copied', 'Upload URL copied to clipboard')
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Bell className="h-8 w-8" />
              Notification Testing Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Manually test work authorization notification cycles with test data
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadTestData}
              disabled={loading}
              className="px-4 py-2 border border-border rounded-lg flex items-center gap-2 hover:bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={cleanupTestData}
              disabled={loading || !testEmployees || testEmployees.length === 0}
              className="px-4 py-2 bg-red-600 text-white rounded-lg flex items-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              Cleanup All
            </button>
          </div>
        </div>
      </div>

      {/* Global Actions */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-3">Batch Actions</h2>
        <div className="flex gap-3">
          <button
            onClick={triggerAllNotifications}
            disabled={triggerLoading || !testEmployees || testEmployees.length === 0}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <Play className="h-4 w-4" />
            {triggerLoading ? 'Processing...' : 'Trigger All Notifications'}
          </button>
          <p className="text-sm text-muted-foreground flex items-center">
            Processes all test employees and sends appropriate notifications based on expiration dates
          </p>
        </div>
      </div>

      {/* No Data State */}
      {!loading && (!testEmployees || testEmployees.length === 0) && (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Test Data Found</h3>
          <p className="text-muted-foreground mb-4">
            Create test employees using the CLI script to get started
          </p>
          <div className="bg-muted rounded-lg p-4 text-left max-w-2xl mx-auto">
            <p className="font-mono text-sm mb-2">
              <span className="text-primary"># Create single test employee</span>
            </p>
            <p className="font-mono text-sm mb-3">
              npm run create-test-workauth -- --days 90 --manager your@email.com
            </p>
            <p className="font-mono text-sm mb-2">
              <span className="text-primary"># Create complete test suite</span>
            </p>
            <p className="font-mono text-sm">
              npm run create-test-workauth -- --scenarios
            </p>
          </div>
        </div>
      )}

      {/* Test Employees Grid */}
      {testEmployees && testEmployees.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Employee List */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">
              Test Employees ({testEmployees.length})
            </h2>

            {testEmployees.map((employee) => (
              <div
                key={employee.id}
                className={`bg-card border rounded-lg p-4 cursor-pointer transition-all ${
                  selectedEmployee?.id === employee.id
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setSelectedEmployee(employee)}
              >
                {/* Employee Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-foreground">{employee.name}</h3>
                    <p className="text-sm text-muted-foreground">{employee.employeeId}</p>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full border flex items-center gap-2 ${getStatusColor(
                      employee.workAuth.daysUntilExpiration
                    )}`}
                  >
                    {getStatusIcon(employee.workAuth.daysUntilExpiration)}
                    <span className="font-semibold">
                      {employee.workAuth.daysUntilExpiration > 0
                        ? `${employee.workAuth.daysUntilExpiration} days`
                        : 'Expired'}
                    </span>
                  </div>
                </div>

                {/* Employee Details */}
                <div className="space-y-1 text-sm mb-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email:</span>
                    <span className="text-foreground">{employee.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Manager:</span>
                    <span className="text-foreground">{employee.managerEmail}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expires:</span>
                    <span className="text-foreground">
                      {new Date(employee.workAuth.expirationDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Notifications:</span>
                    <span className="text-foreground">{employee.workAuth.notificationCount}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-3 border-t border-border">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      triggerNotification(employee.employeeId, false)
                    }}
                    disabled={triggerLoading}
                    className="flex-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    <Mail className="h-3 w-3 inline mr-1" />
                    Send
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      triggerNotification(employee.employeeId, true)
                    }}
                    disabled={triggerLoading}
                    className="flex-1 px-3 py-1.5 text-sm border border-border rounded hover:bg-muted transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className="h-3 w-3 inline mr-1" />
                    Force Resend
                  </button>
                  {employee.workAuth.uploadToken && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        copyUploadUrl(employee.workAuth.uploadToken!)
                      }}
                      className="px-3 py-1.5 text-sm border border-border rounded hover:bg-muted transition-colors"
                      title="Copy upload URL"
                    >
                      📋
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Right Column: Notification History */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">
              Notification History
            </h2>

            {!selectedEmployee ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  Select an employee to view notification history
                </p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="mb-4">
                  <h3 className="font-semibold text-foreground">{selectedEmployee.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {notificationHistory.length} notification(s) sent
                  </p>
                </div>

                {notificationHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <Mail className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm">
                      No notifications sent yet
                    </p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Click "Send" to trigger a notification
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notificationHistory.map((notification) => (
                      <div
                        key={notification.id}
                        className="p-3 bg-muted/50 rounded-lg border border-border"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-primary" />
                            <span className="font-medium text-sm text-foreground">
                              {formatNotificationType(notification.notificationType)}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(notification.sentAt)}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          <strong>To:</strong> {notification.recipientEmail}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default WorkAuthNotificationTestingPage
